import React from 'react';
import {
  Thermometer,
  Wind,
  Compass,
  Gauge,
  Mountain,
  Sun,
  Star,
  Plus,
  Cloud,
  Droplets,
  CloudRain,
  CloudSun,
} from 'lucide-react';
import { VARIABLES } from '@app/components/common/constants';
import { VariableId } from '../types';

interface VariableSelectorProps {
  selectedParam: VariableId;
  favoriteVariables: Set<string>;
  onParamChange: (param: VariableId) => void;
  onOpenPanel?: () => void;
  loading?: boolean;
}

// Variable icon mapping with enhanced wind indicators
const getVariableIcon = (variableId: VariableId) => {
  const iconMap: Record<VariableId, React.ReactNode> = {
    '2t': <Thermometer className="h-4 w-4" />,
    '2dt': (
      <div className="relative">
        <Thermometer className="h-4 w-4" />
        <span className="absolute -top-1 -right-1 bg-pink-500 rounded-full w-3 h-3 flex items-center justify-center">
          <Droplets className="h-2 w-2 text-white" />
        </span>
      </div>
    ),
    precipitation: <CloudRain className="h-4 w-4" />,
    high_cloud_cover: (
      <div className="relative">
        <Cloud className="h-4 w-4" />
        <span className="absolute -top-1 -right-1 text-[8px] font-bold bg-pink-500 text-white rounded-full w-3 h-3 flex items-center justify-center leading-none">
          H
        </span>
      </div>
    ),
    medium_cloud_cover: (
      <div className="relative">
        <Cloud className="h-4 w-4" />
        <span className="absolute -top-1 -right-1 text-[8px] font-bold bg-pink-500 text-white rounded-full w-3 h-3 flex items-center justify-center leading-none">
          M
        </span>
      </div>
    ),
    low_cloud_cover: (
      <div className="relative">
        <Cloud className="h-4 w-4" />
        <span className="absolute -top-1 -right-1 text-[8px] font-bold bg-pink-500 text-white rounded-full w-3 h-3 flex items-center justify-center leading-none">
          L
        </span>
      </div>
    ),
    '10si': (
      <div className="relative">
        <Wind className="h-4 w-4" />
        <span className="absolute -top-1 -right-1 text-[8px] font-bold bg-pink-500 text-white rounded-full w-3 h-3 flex items-center justify-center leading-none">
          10
        </span>
      </div>
    ),
    '10wdir': (
      <div className="relative">
        <Compass className="h-4 w-4" />
        <span className="absolute -top-1 -right-1 text-[8px] font-bold bg-pink-500 text-white rounded-full w-3 h-3 flex items-center justify-center leading-none">
          10
        </span>
      </div>
    ),
    '100si': (
      <div className="relative">
        <Wind className="h-4 w-4" />
        <span className="absolute -top-1 -right-1 text-[8px] font-bold bg-pink-500 text-white rounded-full w-3.5 h-3 flex items-center justify-center leading-none">
          100
        </span>
      </div>
    ),
    '100wdir': (
      <div className="relative">
        <Compass className="h-4 w-4" />
        <span className="absolute -top-1 -right-1 text-[8px] font-bold bg-pink-500 text-white rounded-full w-3.5 h-3 flex items-center justify-center leading-none">
          100
        </span>
      </div>
    ),
    relative_humidity: <Droplets className="h-4 w-4" />,
    msl: <Gauge className="h-4 w-4" />,
    z_500: <Mountain className="h-4 w-4" />,
    fdir: (
      <div className="relative">
        <Sun className="h-4 w-4" />
        <span className="absolute -top-1 -right-1 text-[8px] font-bold bg-pink-500 text-white rounded-full w-3 h-3 flex items-center justify-center leading-none">
          D
        </span>
      </div>
    ),
    ssrd: <Sun className="h-4 w-4" />,
    weather_summary: <CloudSun className="h-4 w-4" />,
  };

  return iconMap[variableId] || <Star className="h-4 w-4" />;
};

// Get variable display name for tooltip
const getVariableTooltipName = (variableId: VariableId) => {
  const variable = VARIABLES.find((v) => v.id === variableId);
  if (!variable) return variableId;

  const nameMap: Record<VariableId, string> = {
    '2t': 'Temperature 2m',
    '2dt': 'Dew Point 2m',
    precipitation: 'Precipitation',
    high_cloud_cover: 'High Cloud Cover',
    medium_cloud_cover: 'Medium Cloud Cover',
    low_cloud_cover: 'Low Cloud Cover',
    '10si': 'Wind Speed 10m',
    '10wdir': 'Wind Direction 10m',
    '100si': 'Wind Speed 100m',
    '100wdir': 'Wind Direction 100m',
    relative_humidity: 'Relative Humidity 2m',
    msl: 'Sea Level Pressure',
    z_500: 'Geopotential 500hPa',
    fdir: 'Direct Solar Radiation',
    ssrd: 'Solar Radiation',
    weather_summary: 'Weather Summary',
  };

  return nameMap[variableId] || variable.label.split(' [')[0];
};

export function VariableSelector({
  selectedParam,
  favoriteVariables,
  onParamChange,
  onOpenPanel,
  loading = false,
}: VariableSelectorProps) {
  // Get favorite variables that support time series
  const timeSeriesVariables = VARIABLES.filter((v) => v.showTimeSeries);
  const favoriteTimeSeriesVariables = timeSeriesVariables.filter((v) =>
    favoriteVariables.has(v.id),
  );

  if (favoriteTimeSeriesVariables.length === 0) {
    return (
      <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg">
        <button
          onClick={onOpenPanel}
          className="px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 text-gray-500 dark:text-gray-400 hover:text-pink-500 dark:hover:text-pink-400"
          disabled={loading || !onOpenPanel}
        >
          Select variables
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg">
      {favoriteTimeSeriesVariables.map((variable) => (
        <div key={variable.id} className="relative group">
          <button
            onClick={() => onParamChange(variable.id as VariableId)}
            className={`
              relative p-1.5 rounded transition-all duration-200
              ${
                selectedParam === variable.id
                  ? 'bg-pink-500 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }
            `}
            disabled={loading}
          >
            <span className={selectedParam === variable.id ? 'text-white' : ''}>
              {getVariableIcon(variable.id as VariableId)}
            </span>
          </button>

          {/* Tooltip */}
          <span className="absolute hidden group-hover:block left-0 top-full mt-1 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap z-10">
            {getVariableTooltipName(variable.id as VariableId)}
          </span>
        </div>
      ))}

      {/* Plus icon to add more variables */}
      {timeSeriesVariables.length > favoriteTimeSeriesVariables.length && onOpenPanel && (
        <div className="relative group">
          <button
            onClick={onOpenPanel}
            className="relative p-1.5 rounded transition-all duration-200 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
            disabled={loading}
          >
            <Plus className="h-4 w-4" />
          </button>

          {/* Tooltip */}
          <span className="absolute hidden group-hover:block left-0 top-full mt-1 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap z-10">
            Add more variables
          </span>
        </div>
      )}
    </div>
  );
}
